import React, { useEffect, useMemo, useState, memo, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Stack,
} from "@mui/material";
import { useAppContext } from "../../context/AppContext";
import { Link } from "react-router-dom";
import { sendAxiosPost } from "../../util/ServerUtil";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import CopyableText from "../../components/CopyableText";

type LogType = "info" | "warning" | "error";
type Category = "auth" | "admin" | "video-data" | "patreon-webhook";

interface FirestoreTimestampLike {
  _seconds: number;
  _nanoseconds: number;
}

interface LogEntry {
  timestamp: FirestoreTimestampLike | string; // supports firestore object or string
  type: LogType;
  category: Category;
  message: string;
  data: string;
}

const columns: GridColDef<LogEntry>[] = [
  // Timestamp is Firestore format: {"_seconds":1769563363,"_nanoseconds":477000000}
  {
    field: "timestamp",
    headerName: "Timestamp",
    width: 220,
    sortable: true,
    renderCell: (params) => {
      const ts = params.value as any;

      // Firestore object
      if (ts && typeof ts === "object" && typeof ts._seconds === "number") {
        const date = new Date(ts._seconds * 1000 + (ts._nanoseconds ?? 0) / 1_000_000);
        return <span style={{ fontFamily: "monospace" }}>{date.toLocaleString()}</span>;
      }

      return "ERROR";
    },
  },
  {
    field: "type",
    headerName: "Type",
    width: 75,
    renderCell: (params) => <span style={{ fontFamily: "monospace" }}>{String(params.value ?? "").toUpperCase()}</span>,
  },
  {
    field: "category",
    headerName: "Category",
    width: 100,
    renderCell: (params) => <span style={{ fontFamily: "monospace" }}>{String(params.value ?? "").toUpperCase()}</span>,
  },
  {
    field: "message",
    headerName: "Message",
    width: 200,
    renderCell: (params) => <span style={{ fontFamily: "monospace" }}>{params.value ?? ""}</span>,
  },
  {
    field: "data",
    headerName: "Data",
    width: 500,
    flex: 2,
    renderCell: (params) => <CopyableText value={params.value ?? ""} previewLength={60} />,
  },
];

const ServerLogs: React.FC = () => {
  const { isAdmin } = useAppContext();

  const [initialLoading, setInitialLoading] = useState(true);
  const [fetching, setFetching] = useState(false);

  const [logs, setLogs] = useState<LogEntry[]>([]);

  // Filters
  const [logTypeFilter, setLogTypeFilter] = useState<LogType | "all">("all");
  const [categoryFilter, setCategoryFilter] = useState<Category | "all">("all");
  const [search, setSearch] = useState("");

  // Fetch qty
  const [qty, setQty] = useState<number>(100);

  const fetchLogs = useCallback(async (limit: number) => {
    const safeLimit = Number.isFinite(limit) ? Math.max(1, Math.min(100_000, Math.floor(limit))) : 100;
    const resp = await sendAxiosPost("/api/getLogs", { limit: safeLimit }, true);
    setLogs((resp.data ?? []) as LogEntry[]);
  }, []);

  useEffect(() => {
    document.title = "Server Logs | ipwd.club";
    (async () => {
      try {
        await fetchLogs(100);
      } catch (e) {
        console.error(e);
      } finally {
        setInitialLoading(false);
      }
    })();
  }, [fetchLogs]);

  const onRetrieve = async () => {
    setFetching(true);
    try {
      await fetchLogs(qty);
    } catch (e) {
      console.error(e);
    } finally {
      setFetching(false);
    }
  };

  const filteredLogs = useMemo(() => {
    const q = search.trim().toLowerCase();

    return (logs ?? []).filter((l) => {
      if (logTypeFilter !== "all" && l.type !== logTypeFilter) return false;
      if (categoryFilter !== "all" && l.category !== categoryFilter) return false;

      if (!q) return true;

      const msg = (l.message ?? "").toLowerCase();
      const data = (l.data ?? "").toLowerCase();
      return msg.includes(q) || data.includes(q);
    });
  }, [logs, logTypeFilter, categoryFilter, search]);

  // Stable row id (DataGrid needs an id; we synthesize one from timestamp+fields)
  const getRowId = useCallback((row: LogEntry) => {
    const ts = row.timestamp as any;
    let tsKey = "";

    if (ts && typeof ts === "object" && typeof ts._seconds === "number") {
      tsKey = `${ts._seconds}.${ts._nanoseconds ?? 0}`;
    } else {
      tsKey = String(ts ?? "");
    }

    // include a few fields to reduce collision risk
    return `${tsKey}|${row.type}|${row.category}|${row.message ?? ""}`;
  }, []);

  if (!isAdmin) {
    return (
      <Container sx={{ textAlign: "center", mt: 4 }}>
        <Typography variant="h4">Access Denied</Typography>
        <Typography>You do not have permission to access this page.</Typography>
      </Container>
    );
  }

  if (initialLoading) {
    return (
      <Container sx={{ textAlign: "center", mt: 4 }}>
        <Typography variant="h4">Loading...</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4, display: "flex", flexDirection: "column", alignItems: "stretch", gap: 2 }}>
      <Container sx={{ mb: 2 }}>
        <Link to="/admin/ServerLogs" className="btn-active">
          Server Logs
        </Link>
        <Link to="/admin/userManager" className="btn">
          User Manager
        </Link>
        <Link to="/admin/videoDataManager" className="btn">
          Video Data Manager
        </Link>
      </Container>

      <Typography variant="h4" gutterBottom>
        Server Logs
      </Typography>

      {/* Controls */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="logTypeFilterLabel">Log Type</InputLabel>
            <Select
              labelId="logTypeFilterLabel"
              label="Log Type"
              value={logTypeFilter}
              onChange={(e) => setLogTypeFilter(e.target.value as LogType | "all")}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="info">INFO</MenuItem>
              <MenuItem value="warning">WARNING</MenuItem>
              <MenuItem value="error">ERROR</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="categoryFilterLabel">Category</InputLabel>
            <Select
              labelId="categoryFilterLabel"
              label="Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as Category | "all")}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="auth">AUTH</MenuItem>
              <MenuItem value="admin">ADMIN</MenuItem>
              <MenuItem value="video-data">VIDEO-DATA</MenuItem>
              <MenuItem value="patreon-webhook">PATREON-WEBHOOK</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Search (message or data)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 260 }}
          />

          <TextField
            size="small"
            label="Retrieve qty"
            type="number"
            value={qty}
            onChange={(e) => setQty(Number(e.target.value))}
            inputProps={{ min: 1, max: 100000, step: 1 }}
            sx={{ width: 160 }}
          />

          <Button
            variant="contained"
            onClick={onRetrieve}
            disabled={fetching}
            sx={{ whiteSpace: "nowrap" }}
          >
            {fetching ? "Retrieving..." : "Retrieve Logs"}
          </Button>
        </Stack>

        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          Showing {filteredLogs.length} of {logs?.length ?? 0} logs
        </Typography>
      </Box>

      {/* Grid */}
      {logs && logs.length > 0 ? (
        <DataGrid
          rows={filteredLogs}
          columns={columns}
          getRowId={getRowId}
          pageSizeOptions={[50, 100, 500, 1000, 5000]}
          disableRowSelectionOnClick
          getRowClassName={(params) => {
            const t = (params.row as LogEntry).type;
            if (t === "error") return "log-row--error";
            if (t === "warning") return "log-row--warning";
            return "log-row--info";
          }}
          sx={{
            border: 0,

            // Make grid text default to monospace if you want (optional)
            "& .MuiDataGrid-cell": { fontFamily: "monospace" },

            // INFO (white-ish)
            "& .log-row--info": {
              color: "#e0ffd4",
            },

            // WARNING (yellow)
            "& .log-row--warning": {
              color: "#ffeb3b",
            },

            // ERROR (red)
            "& .log-row--error": {
              color: "#f44336",
            },

            // Ensure links/icons inside the row inherit the row color
            "& .log-row--info a, & .log-row--warning a, & .log-row--error a": {
              color: "inherit",
            },
            "& .log-row--info svg, & .log-row--warning svg, & .log-row--error svg": {
              color: "inherit",
            },

            // Keep hover/selected backgrounds but don’t override text color
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(255,255,255,0.06)",
            },
            "& .MuiDataGrid-row.Mui-selected": {
              backgroundColor: "rgba(255,255,255,0.10)",
            },
            "& .MuiDataGrid-row.Mui-selected:hover": {
              backgroundColor: "rgba(255,255,255,0.14)",
            },
          }}
        />
      ) : (
        <Typography>No logs available.</Typography>
      )}
    </Container>
  );
};

export default memo(ServerLogs);
